// Snabbsök display-label rendering — the SINGLE source of truth for the string
// a result row shows the user. Extracted from snabb-sok.tsx (REG-6). Pure
// functions, no React — safe to import anywhere.
//
// NOTE: the eval harness (tools/eval/) does NOT import this module — the ext
// evals call baseTitle(h.title) directly, so eval-report labels for IM rows
// can differ from what users see (topic_label) since the aug 2026 fix. The
// pm-recall/pattern evals match on URLs/raw titles and are unaffected.
//
// The label pipeline:
//   displayTitle(h, isIM) — IM: the pipeline-denormalized `topic_label` (exact
//                           article name; fallback baseTitle for older index
//                           data). External: strip leading source name +
//                           recognised section suffix (stripSourcePrefix +
//                           baseTitle) — topic_label is deliberately NOT used
//                           there (LMB/RCC carry group-header names in it).
//   disambiguateSameSource(hits, isIM) — for SAME-source rows that resolve to an
//                           identical display title, append a " (qualifier)" from
//                           the differing URL path segment (REG-6). This is what
//                           the user actually sees.
import {
	baseTitle,
	imDisplayLabel,
	stripSourcePrefix,
} from './snabbsok-titles.ts'
import { type SnabbHit } from './snabbsok.ts'

/**
 * Clean a hit's display title: strip the section suffix for IM, and for
 * external also strip the leading source name (the coloured block shows it).
 */
export function displayTitle(h: SnabbHit, isIM: boolean): string {
	let title = h.title || h.url
	// IM: the pipeline denormalizes `topic_label` = the exact article name onto
	// every doc — use it directly instead of string-stripping the chunk title.
	// baseTitle()'s SECTION_VOCAB whitelist misses ~38% of IM section headings
	// ("Klinik och symtom", "Anamnes", numbered h3s, near-echo first-h2s like
	// "DVT och lungemboli vid graviditet"), leaking section suffixes into what
	// should read as the clean article title. Fallback keeps old behaviour when
	// topic_label is absent (older index data).
	if (isIM) return imDisplayLabel(h.topic_label, title)
	if (h.source) title = stripSourcePrefix(title, h.source)
	return baseTitle(title)
}

// ── REG-6: same-source duplicate-label disambiguation ──────────────────────
// Distinct documents from the SAME source can resolve to an identical display
// label (the bare disease name) — e.g. 1177 has both a "kliniska kunskapsstöd"
// AND a "vårdförlopp" page titled "Hjärtsvikt"; LMB has "Epilepsi" both under
// nervsystemets-sjukdomar AND under sjukdomar-under-graviditet. The colour chip
// can't tell them apart (same source → same colour), so two rows read as one
// broken duplicate. We append a breadcrumb qualifier derived from the URL path
// segment that DIFFERS between the colliding pages, e.g. "Hjärtsvikt (vårdförlopp)".
//
// CROSS-source same-title rows (1177 + VISS + Region all "Hjärtsvikt") are left
// alone — there the colour chip + abbrev already disambiguate, and showing the
// same disease across sources is the point. DISPLAY-ONLY: this never reorders,
// never collapses (both pages are real distinct docs), and never touches which
// chunk represents a page — so ranking / pm_recall are unaffected.
//
// The qualifier comes from the URL, NOT the title's section: same-source twins
// often share a section label (LMB epilepsi pair are both "Epilepsi – Inledning"),
// so only the path distinguishes them.

// Known 1177/LMB path segments → clean Swedish label. Anything else falls back
// to a slug→spaced lowercase transform, so an unseen path shape still reads
// sensibly rather than as a raw slug.
const PATH_SEGMENT_LABELS: Record<string, string> = {
	'kliniska-kunskapsstod': 'kliniskt kunskapsstöd',
	vardforlopp: 'vårdförlopp',
	omvardnadsprogram: 'omvårdnad',
	proteskirurgi: 'proteskirurgi',
	primarvard: 'primärvård',
	'sjukdomar-under-graviditet': 'under graviditet',
	'nervsystemets-sjukdomar': 'nervsystemets sjukdomar',
	'hud--och-konssjukdomar': 'hud och könssjukdomar',
}

function humanizePathSegment(seg: string): string {
	if (PATH_SEGMENT_LABELS[seg]) return PATH_SEGMENT_LABELS[seg]
	// slug → spaced lowercase. Collapse LMB's literal "--" first, then single '-'.
	return seg.replace(/--/g, ' ').replace(/-/g, ' ').replace(/\s+/g, ' ').trim()
}

/** Path segments of a url (no scheme/host, decoded, lowercased, empties dropped). */
function urlPathSegments(url: string): string[] {
	try {
		return new URL(url).pathname
			.split('/')
			.filter(Boolean)
			.map(s => decodeURIComponent(s).toLowerCase())
	} catch {
		return []
	}
}

/**
 * Given the page_urls of a same-label same-source collision group, return a
 * qualifier per url (parallel array) — the most-specific path segment that
 * tells it apart from the others. Strategy:
 *   1. strip the shared disease "stem" (common leaf-prefix) — so a differing
 *      leaf like "hoftledsartros---proteskirurgi" reduces to "proteskirurgi";
 *   2. else use the deepest differing ANCESTOR segment (the category, e.g.
 *      "kliniska-kunskapsstod" / "nervsystemets-sjukdomar");
 *   3. else the differing leaf itself.
 * Returns '' for a member with no usable differentiator (caller leaves it
 * unqualified rather than emit an empty "()").
 */
function pathQualifiers(urls: string[]): string[] {
	const segLists = urls.map(urlPathSegments)
	// Segments common to EVERY member — never a distinguisher.
	const common = new Set(segLists[0] ?? [])
	for (const segs of segLists.slice(1)) {
		for (const s of [...common]) if (!segs.includes(s)) common.delete(s)
	}
	// Disease "stem" = the leading DASH-TOKENS shared by every member's LAST
	// segment, compared token-wise (not char-wise) so we never cut mid-word.
	// e.g. leaves "hoftledsartros---proteskirurgi" & "hoftledsartros---primarvard"
	// share tokens ["hoftledsartros","",""] (the "---" yields empty tokens),
	// leaving "proteskirurgi"/"primarvard" as the distinguishing suffix — whereas
	// a char-prefix would stop at "hoftledsartros---pr" and mangle it.
	const leaves = segLists.map(s => s[s.length - 1] ?? '')
	const tokenLists = leaves.map(lf => lf.split('-'))
	let sharedTokens = tokenLists[0]?.length ?? 0
	for (const toks of tokenLists.slice(1)) {
		let i = 0
		const ref = tokenLists[0] ?? []
		while (i < sharedTokens && i < toks.length && toks[i] === ref[i]) i++
		sharedTokens = i
	}
	// Rebuild the stem from the shared leading tokens (rejoin with '-'), then
	// drop trailing empties from a "---" run so the suffix starts clean.
	const stemTokens = (tokenLists[0] ?? []).slice(0, sharedTokens)
	const diseaseStem = stemTokens.join('-').replace(/-+$/, '')

	return segLists.map((segs, idx) => {
		const leaf = leaves[idx]
		// 1) leaf suffix after the shared disease stem (token boundary)
		if (diseaseStem && leaf.startsWith(diseaseStem) && leaf.length > diseaseStem.length) {
			const suffix = leaf.slice(diseaseStem.length).replace(/^-+/, '')
			if (suffix) return humanizePathSegment(suffix)
		}
		// 2) deepest differing ancestor (exclude the leaf + all-common segments)
		const ancestors = segs.slice(0, -1).filter(s => !common.has(s))
		if (ancestors.length) return humanizePathSegment(ancestors[ancestors.length - 1])
		// 3) the differing leaf itself
		if (leaf && !common.has(leaf)) return humanizePathSegment(leaf)
		return ''
	})
}

/**
 * Build a Map<hit, displayLabel> for one section. Hits whose (source + display
 * title) is unique map to their plain display title. For any same-source group
 * sharing an identical display title (≥2 members), append " (qualifier)" from
 * pathQualifiers. IM hits rarely collide (distinct article names; since the
 * aug 2026 topic_label fix this rests on index data rather than code, and a
 * collision would be qualified via the URL path like any same-source pair),
 * and a source-less hit can't be grouped, so both pass through unqualified.
 */
export function disambiguateSameSource(
	hits: SnabbHit[],
	isIM: boolean,
): Map<SnabbHit, string> {
	const labels = new Map<SnabbHit, string>()
	const groups = new Map<string, SnabbHit[]>()
	for (const h of hits) {
		const title = displayTitle(h, isIM)
		labels.set(h, title) // default: plain title
		const key = `${h.source ?? ''} ${title.toLowerCase()}`
		const g = groups.get(key)
		if (g) g.push(h)
		else groups.set(key, [h])
	}
	for (const members of groups.values()) {
		if (members.length < 2 || !members[0].source) continue
		const quals = pathQualifiers(
			members.map(m => (m.page_url || m.url || '').split('#')[0]),
		)
		members.forEach((m, i) => {
			const q = quals[i]
			if (q) labels.set(m, `${displayTitle(m, isIM)} (${q})`)
		})
	}
	return labels
}
